import { checkPost, postLength, POST_LIMITS, type PostIssue } from '@ly360/core';
import { ChannelIcon, DateTimeField, ErrorState, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ImagePlus, Loader2, X } from 'lucide-react';
import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, upload } from '../lib/api';
import { iconFor } from '../integrations/Connect';

export interface Destination {
  id: string;
  provider: string;
  channel: string;
  displayName: string;
  status: string;
}
export interface MediaItem {
  id: string;
  url: string;
  mime: string;
  name: string;
}
export interface PostDraft {
  body: string;
  media: MediaItem[];
  linkUrl: string;
  targets: string[];
  overrides: Record<string, string>;
  when: 'now' | 'later' | 'draft';
  scheduledAt: string;
}
export const emptyDraft = (): PostDraft => ({
  body: '',
  media: [],
  linkUrl: '',
  targets: [],
  overrides: {},
  when: 'now',
  scheduledAt: '',
});

export const useDestinations = () =>
  useQuery({
    queryKey: ['destinations'],
    queryFn: () => api<{ items: Destination[] }>('/v1/publishing/destinations'),
  });

/** Problems per selected destination (errors stop publishing; warnings are advice). */
export function draftIssues(draft: PostDraft, destinations: Destination[]) {
  return draft.targets.map((id) => {
    const d = destinations.find((x) => x.id === id);
    const text = draft.overrides[id] ?? draft.body;
    return {
      id,
      destination: d,
      ...(d ? checkPost(d.provider, text, draft.media) : { errors: [], warnings: [] }),
    };
  });
}

/** Every account that can publish, as logo tiles: tick as many as you like (or all). */
export function DestinationPicker({
  value,
  onChange,
  destinations,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  destinations: Destination[];
}) {
  const { t } = useTranslation();
  const all = destinations.length > 0 && destinations.every((d) => value.includes(d.id));
  if (!destinations.length)
    return (
      <p className="notice warn" style={{ margin: 0 }}>
        <span className="grow">{t('publishing.noDestinations')}</span>
        <Link className="btn btn-sm" to="/admin/integrations">
          {t('nav.integrations')}
        </Link>
      </p>
    );
  return (
    <div className="dest-picker" data-testid="destinations">
      <div className="dest-head">
        <span className="faint">{t('publishing.selected', { count: value.length })}</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onChange(all ? [] : destinations.map((d) => d.id))}
          data-testid="select-all"
        >
          {all ? t('publishing.selectNone') : t('publishing.selectAll')}
        </button>
      </div>
      <div className="dest-grid" role="group" aria-label={t('publishing.destinations')}>
        {destinations.map((d) => {
          const on = value.includes(d.id);
          return (
            <label key={d.id} className={`dest-tile${on ? ' is-on' : ''}`} data-testid={`dest-${d.provider}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={(e) =>
                  onChange(e.target.checked ? [...value, d.id] : value.filter((x) => x !== d.id))
                }
              />
              <ChannelIcon channel={iconFor(d.provider)} size={16} />
              <span className="dest-name">
                <strong dir="auto">{d.displayName}</strong>
                <span className="faint">{t(`crm.integrations.providers.${d.provider}`)}</span>
              </span>
              {on && <CheckCircle2 size={16} className="dest-check" aria-hidden="true" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function issueText(t: (k: string, o?: Record<string, unknown>) => string, i: PostIssue) {
  return t(`publishing.issue.${i.code}`, i as unknown as Record<string, unknown>);
}

/**
 * Write once, post everywhere: one text (with an optional wording per platform), photos or a video, and every
 * destination checked live against its platform's limits.
 */
export function PostComposer({
  draft,
  setDraft,
  destinations,
  withSchedule = true,
}: {
  draft: PostDraft;
  setDraft: Dispatch<SetStateAction<PostDraft>>;
  destinations: Destination[];
  withSchedule?: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const file = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [tab, setTab] = useState<string>('all');
  const set = (p: Partial<PostDraft>) => setDraft((d) => ({ ...d, ...p }));
  const selected = destinations.filter((d) => draft.targets.includes(d.id));
  const issues = draftIssues(draft, destinations);
  const active = tab === 'all' ? null : selected.find((d) => d.id === tab);
  const text = active ? (draft.overrides[active.id] ?? draft.body) : draft.body;
  const tightest = selected.reduce<{ max: number; provider: string } | null>((m, d) => {
    const l = POST_LIMITS[d.provider];
    return l && (!m || l.maxChars < m.max) ? { max: l.maxChars, provider: d.provider } : m;
  }, null);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    setUploadError(null);
    for (const f of [...files].slice(0, 10 - draft.media.length)) {
      setUploading((n) => n + 1);
      try {
        const r = await upload<{ id: string; url: string; mime: string; name: string }>(
          `/v1/media?name=${encodeURIComponent(f.name)}`,
          f,
        );
        setDraft((d) => ({
          ...d,
          media: [...d.media, { id: r.id, url: r.url, mime: r.mime, name: r.name }],
        }));
      } catch (e) {
        setUploadError(e);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  return (
    <div className="composer-post" data-testid="post-composer">
      <DestinationPicker
        value={draft.targets}
        onChange={(v) => set({ targets: v })}
        destinations={destinations}
      />

      {selected.length > 0 && (
        <div className="tabs" role="tablist" aria-label={t('publishing.wording')}>
          <button type="button" role="tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>
            {t('publishing.everywhere')}
          </button>
          {selected.map((d) => (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={tab === d.id}
              onClick={() => setTab(d.id)}
            >
              <ChannelIcon channel={iconFor(d.provider)} size={11} /> {d.displayName}
              {draft.overrides[d.id] !== undefined && <span className="tab-count">✎</span>}
            </button>
          ))}
        </div>
      )}
      <label className="sr-only" htmlFor="post-text">
        {t('publishing.text')}
      </label>
      <textarea
        id="post-text"
        className="textarea"
        rows={6}
        dir="auto"
        placeholder={
          active ? t('publishing.overrideHint', { name: active.displayName }) : t('publishing.textHint')
        }
        value={text}
        onChange={(e) =>
          active
            ? set({ overrides: { ...draft.overrides, [active.id]: e.target.value } })
            : set({ body: e.target.value })
        }
        data-testid="post-text"
      />
      <div className="post-meta">
        {active ? (
          <>
            <span
              className={
                postLength(active.provider, text) > (POST_LIMITS[active.provider]?.maxChars ?? Infinity)
                  ? 'overdue'
                  : 'faint'
              }
            >
              {fmt.number(postLength(active.provider, text))} /{' '}
              {fmt.number(POST_LIMITS[active.provider]?.maxChars ?? 0)}
            </span>
            {draft.overrides[active.id] !== undefined && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const o = { ...draft.overrides };
                  delete o[active.id];
                  set({ overrides: o });
                }}
              >
                {t('publishing.useMain')}
              </button>
            )}
          </>
        ) : (
          tightest && (
            <span className="faint">
              {t('publishing.shortest', {
                max: fmt.number(tightest.max),
                platform: t(`crm.integrations.providers.${tightest.provider}`),
              })}
            </span>
          )
        )}
      </div>

      <div className="post-media">
        {draft.media.map((m) => (
          <figure key={m.id} className="media-thumb">
            {m.mime.startsWith('video/') ? <video src={m.url} muted /> : <img src={m.url} alt={m.name} />}
            <button
              type="button"
              className="icon-btn"
              aria-label={t('common.remove')}
              onClick={() => set({ media: draft.media.filter((x) => x.id !== m.id) })}
            >
              <X size={12} />
            </button>
          </figure>
        ))}
        <button
          type="button"
          className="media-add"
          onClick={() => file.current?.click()}
          disabled={draft.media.length >= 10}
          data-testid="add-media"
        >
          {uploading ? <Loader2 size={18} className="spin" /> : <ImagePlus size={18} />}
          <span>{t('publishing.addMedia')}</span>
        </button>
        <input
          ref={file}
          type="file"
          hidden
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
          onChange={(e) => void addFiles(e.target.files).then(() => (e.target.value = ''))}
          data-testid="media-input"
        />
      </div>
      {!!uploadError && <ErrorState error={uploadError} />}

      <label className="field">
        <span>{t('publishing.link')}</span>
        <input
          className="input"
          type="url"
          dir="ltr"
          placeholder="https://"
          value={draft.linkUrl}
          onChange={(e) => set({ linkUrl: e.target.value })}
        />
      </label>

      {issues.some((i) => i.errors.length || i.warnings.length) && (
        <ul className="post-issues" data-testid="post-issues">
          {issues
            .filter((i) => i.errors.length || i.warnings.length)
            .map((i) => (
              <li key={i.id} className={i.errors.length ? 'is-error' : 'is-warning'}>
                <ChannelIcon channel={iconFor(i.destination?.provider ?? '')} size={11} />
                <strong dir="auto">{i.destination?.displayName}</strong>
                <span>{[...i.errors, ...i.warnings].map((x) => issueText(t, x)).join(' · ')}</span>
              </li>
            ))}
        </ul>
      )}

      {withSchedule && (
        <div className="post-when">
          <div className="segmented" role="group" aria-label={t('publishing.when')}>
            {(['now', 'later', 'draft'] as const).map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={draft.when === w}
                onClick={() => set({ when: w })}
                data-testid={`when-${w}`}
              >
                {t(`publishing.whenOptions.${w}`)}
              </button>
            ))}
          </div>
          {draft.when === 'later' && (
            <DateTimeField
              id="post-at"
              value={draft.scheduledAt}
              onChange={(v) => set({ scheduledAt: v })}
              aria-label={t('publishing.scheduledAt')}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** The request body for a draft (wall time converted to ISO in the organisation's zone). */
export function draftPayload(draft: PostDraft, toIso: (wall: string) => string | null) {
  return {
    body: draft.body,
    media: draft.media.map((m) => ({ assetId: m.id })),
    linkUrl: draft.linkUrl.trim() || null,
    targets: draft.targets.map((id) => ({ accountId: id, body: draft.overrides[id] ?? null })),
    publishNow: draft.when === 'now',
    scheduledAt: draft.when === 'later' ? toIso(draft.scheduledAt) : null,
  };
}
